<template>
  <ParentNode
    :id="id"
    :elements="elements"
    :source-connection-active="sourceConnectionActive"
    :target-connection-active="targetConnectionActive"
    :type="type"
    :get-connected-elements="getConnectedElements"
    :get-connected-source-elements="getConnectedSourceElements"
    :data="data"
    :dimensions="dimensions"
    :add-new-node="addNewNode"
    :new-node="newNode">
    <template #body>
      <div
        :class="[
          { 'shadow-[#5CE0A9]': data.type == 'trigger' && (selectedNode?.id == id || newNode?.id == id) },
          { 'shadow-[#B171F9]': data.type == 'action' && (selectedNode?.id == id || newNode?.id == id) },
          { 'shadow-[#59BEE5]': data.type == 'wait' && (selectedNode?.id == id || newNode?.id == id) },
          { 'shadow-[#F97171]': data.type == 'end_sequence' && (selectedNode?.id == id || newNode?.id == id) },
          { 'shadow-[#FF1BF6]': data.type == 'parallel' && (selectedNode?.id == id || newNode?.id == id) },
          { 'shadow-[#356578]': data.type == 'for' && (selectedNode?.id == id || newNode?.id == id) },
          { 'shadow-[#FFBE15]': (data.type == 'switch' || data.type == 'condition') && (selectedNode?.id == id || newNode?.id == id) },
          { 'bg-[#5CE0A9]': data.type == 'trigger' },
          { 'bg-[#B171F9]': data.type == 'action' },
          { 'bg-[#59BEE5]': data.type == 'wait' },
          { 'bg-[#F97171]': data.type == 'end_sequence' },
          { 'bg-[#FF1BF6]': data.type == 'parallel' || data.type == 'end_parallel' },
          { 'bg-[#356578]': data.type == 'for' || data.type == 'end_for_loop' },
          { 'bg-[#FFBE15]': data.type == 'switch' || data.type == 'condition' },
          { 'shadow-playbook ': selectedNode?.id == id || newNode?.id == id }
        ]"
        class="min-h-[8rem] shadow-lg transition-all ease-in-out w-96 text-center rounded-lg relative !overflow-hidden">
        <!-- Colored header section -->
        <div class="h-4" />

        <div
          v-if="record_passed_or_stays_here"
          :class="[
            { 'border-[3px] border-dotted !border-[#5CE0A9]': data.type == 'trigger' },
            { 'border-[3px] border-dotted !border-[#B171F9]': data.type == 'action' },
            { 'border-[3px] border-dotted !border-[#59BEE5]': data.type == 'wait' },
            { 'border-[3px] border-dotted !border-[#F97171]': data.type == 'end_sequence' },
            { 'border-[3px] border-dotted !border-[#FF1BF6]': data.type == 'parallel' || data.type == 'end_parallel' },
            { 'border-[3px] border-dotted !border-[#356578]': data.type == 'for' || data.type == 'end_for_loop' },
            { 'border-[3px] border-dotted !border-[#FFBE15]': data.type == 'switch' || data.type == 'condition' }
          ]"
          class="absolute w-[105%] h-[115%] -ml-[2.5%] -mt-[2.5%] rounded-lg" />

        <div v-if="record_stays_here" class="left-[102%] top-[40%] absolute flex items-center text-sm">
          <div
            class="w-5 h-1 border-b"
            :class="[
              { ' !border-[#5CE0A9]': data.type == 'trigger' },
              { ' !border-[#B171F9]': data.type == 'action' },
              { ' !border-[#59BEE5]': data.type == 'wait' },
              { ' !border-[#F97171]': data.type == 'end_sequence' },
              { ' !border-[#FF1BF6]': data.type == 'parallel' || data.type == 'end_parallel' },
              { ' !border-[#356578]': data.type == 'for' || data.type == 'end_for_loop' }
            ]" />

          <div
            class="border-[3px] bg-white w-[calc(100%+50px)] flex space-x-2 items-center border-dotted p-2 whitespace-nowrap rounded-lg"
            :class="[
              { ' !border-[#5CE0A9]': data.type == 'trigger' },
              { ' !border-[#B171F9]': data.type == 'action' },
              { ' !border-[#59BEE5]': data.type == 'wait' },
              { ' !border-[#F97171]': data.type == 'end_sequence' },
              { ' !border-[#FF1BF6]': data.type == 'parallel' || data.type == 'end_parallel' },
              { ' !border-[#356578]': data.type == 'for' || data.type == 'end_for_loop' },
              { ' !border-[#FFBE15]': data.type == 'switch' || data.type == 'condition' }
            ]">
            <div class="w-9">
              <img
                onerror="this.style.display='none'"
                class="inline-block object-contain rounded-full !h-9 !min-h-9 !max-h-9 w-9 !min-w-9 !max-w-9"
                :src="`https://logosnap.io/api/${$utils.getDomainName(
                  record_here?.values[$utils.getOptionMetricReference('company', 'website')]
                )}?token=ecoTB81m5dQqkrjw`"
                alt="logo">
            </div>
            <div>{{ getRecord(filtered_company_id)?.name || record_here?.name }} {{ $t("is here") }}</div>
          </div>
        </div>

        <div class="bg-white rounded-t-lg mt-0">
          <div class="px-4 py-4 transition-all ease-in-out text-start">
            <div class="text-sm text-common-grey">
              {{
                data.type == "trigger"
                  ? $t("When this happens...")
                  : data.type == "switch"
                    ? $t("switch")
                    : data.type == "condition"
                      ? condition_number == 1
                        ? $t("check this case first")
                        : $t("if case") + ` ${condition_number - 1} ` + $t("is false, do this case")
                      : node_title == "End Switch"
                        ? $t("If all other cases are false")
                        : $t("Do this...")
              }}
            </div>
            <div v-if="data.flowObjectType" class="px-2 py-1 mt-2 inline-block text-sm bg-gray-100 rounded-full float-right capitalize p-1">
              {{ data.type }}
            </div>

            <div class="flex items-start mt-2 space-x-3 clear-both">
              <div
                :class="[
                  { 'bg-green-50': data.type == 'trigger' },
                  { 'bg-purple-50': data.type == 'action' },
                  { 'bg-blue-50': data.type == 'wait' },
                  { 'bg-red-50': data.type == 'end_sequence' },
                  { 'bg-yellow-50': data.type == 'switch' },
                  { 'bg-[#FF1BF650]': data.type == 'parallel' || data.type == 'end_parallel' },
                  { 'bg-[#35657830]': data.type == 'for' || data.type == 'end_for_loop' }
                ]"
                class="flex items-center justify-center rounded-md w-14 h-14 flex-shrink-0">
                <PlaybookGoalIcon v-if="data.type == 'trigger'" />
                <Funnel v-else-if="data.values?.sub_item?.startsWith('filter_')" class="w-8 h-8 text-[#B171F9]" />
                <PlaybookActionIcon v-else-if="data.type == 'action'" />
                <Timer v-else-if="data.type == 'wait'" class="w-8 h-8" color="#59BEE5" />
                <PlaybookEndIcon
                  v-if="data.type.startsWith('end')"
                  :color="data.type == 'end_for_loop' ? '#356578' : data.type == 'end_parallel' ? '#FF1BF6' : '#CC4F4F'"
                  class="w-8 h-8" />
                <Split v-else-if="data.type == 'parallel'" class="w-8 h-8 rotate-180" color="#E816E0" />
                <Repeat2 v-else-if="data.type == 'for'" class="w-8 h-8" color="#005779" />
                <ArrowDownUp v-else-if="data.type == 'switch'" class="w-8 h-8" color="#FFBE15" />
                <div v-if="data.type == 'condition'" class="text-xl bg-yellow-50 p-2 px-4 text-[#FFBE15] rounded-md">
                  {{ condition_number }}
                </div>
              </div>

              <div class="min-w-0 flex-1">
                <div :class="[{ 'text-sm': data.type == 'condition' }]" class="text-lg font-bold capitalize break-words">
                  {{ node_title }}
                </div>
                <div class="text-sm text-light break-words">
                  <div v-html="html_text_calc" />
                </div>
              </div>
            </div>
            <div
              :class="[
                { 'green-bottom-shadow': data.type == 'trigger' },
                { 'purple-bottom-shadow': data.type == 'action' },
                { 'blue-bottom-shadow': data.type == 'wait' },
                { 'red-bottom-shadow': data.type == 'end_sequence' },
                { 'magenta-bottom-shadow': data.type == 'parallel' || data.type == 'end_parallel' },
                { 'navy-bottom-shadow': data.type == 'for' || data.type == 'end_for_loop' }
              ]"
              class="mt-4 w-full" />
          </div>
        </div>
      </div>
    </template>
  </ParentNode>
</template>

<script>
import ParentNode from "./ParentNode.vue"
import PlaybookGoalIcon from "@/components/shared/icons/PlaybookGoalIcon.vue"
import PlaybookActionIcon from "@/components/shared/icons/PlaybookActionIcon.vue"
import PlaybookEndIcon from "@/components/shared/icons/PlaybookEndIcon.vue"
import { transformServerFilterToMetricFilter } from "@/services/data.transformers"
import { ArrowDownUp, Split, Repeat2, Funnel, Timer } from "lucide-vue-next"

export default {
  components: {
    ParentNode,
    PlaybookGoalIcon,
    PlaybookActionIcon,
    Funnel,
    Timer,
    PlaybookEndIcon,
    Split,
    Repeat2,
    ArrowDownUp
  },
  props: {
    newNode: null,
    addNewNode: null,
    filteredCompanyId: null,
    selectedNode: null,
    data: null,
    type: null,
    dimensions: null,
    sourceConnectionActive: null,
    targetConnectionActive: null,
    id: null,
    getConnectedElements: null,
    getConnectedSourceElements: null,
    elements: null
  },
  data() {
    return {
      record_here: null
    }
  },
  computed: {
    condition_number() {
      const connection_to_switch = this.elements.find((el) => el.target == this.id)
      const switch_or_split_component = this.elements.find((el) => el.id == connection_to_switch?.source)
      const all_cases_connected = this.elements
        .filter((el) => el.source == switch_or_split_component?.id && !(el.targetNode?.data?.type == "end_branch"))
        .map((el) => el.targetNode)
      const case_index = all_cases_connected.findIndex((c) => c?.id == this.id)
      return case_index + 1
    },
    filtered_company_id() {
      return this.filteredCompanyId
    },
    record_passed_or_stays_here() {
      return this.data.records_passed?.includes(this.filteredCompanyId) || this.data.records_here?.includes(this.filteredCompanyId)
    },
    record_stays_here() {
      return this.data.records_here?.includes(this.filteredCompanyId)
    },
    html_text_calc() {
      const selected_trigger_obj = this.$store.state.data.playbook_triggers.find(
        (trigger) => trigger.value == this.data.values?.selected_trigger
      )
      let text_calc = "<span>"
      if (this.data.type == "trigger") {
        if (
          (!this.data.values.selected_trigger && this.data.values?.sub_item !== "metric") ||
          (this.data.values?.sub_item == "metric" && !this.data.values.metric_filter)
        ) {
          text_calc += " (" + this.$t("Click to configure trigger") + ") "
        }
        let selected_trigger_text_calc
        if (this.data.values.selected_trigger) {
          if (selected_trigger_obj.alt_value_for_any_selection && this.data.values.selected_trigger_value?.toLowerCase() == "any") {
            selected_trigger_text_calc = selected_trigger_obj.alt_value_for_any_selection
              ?.replaceAll("_", " ")
              .replaceAll("is", this.data.values?.sub_item)
          } else {
            selected_trigger_text_calc = this.data.values.selected_trigger
              ?.replaceAll("_", " ")
              .replaceAll("is", this.data.values?.sub_item)
          }
          if (this.data.values?.selected_items?.length > 1) {
            selected_trigger_text_calc = selected_trigger_text_calc
              .replaceAll("has", "have")
              .replaceAll("is", "are")
              .replaceAll("_", " ")
              .replaceAll("is", this.data.values?.sub_item)
          }
          text_calc += `${selected_trigger_text_calc}`
        }
        if (this.data.values.selected_trigger_value) {
          if (selected_trigger_obj.alt_value_for_any_selection && this.data.values.selected_trigger_value.toLowerCase() == "any") {
            text_calc = `${text_calc.replaceAll("ANY", ` <span class="font-bold">any</span>`)}`
          } else if (text_calc.includes("X")) {
            text_calc = `${text_calc.replaceAll("X", ` <span class="font-bold">${this.data.values.selected_trigger_value}</span>`)}`
          } else {
            text_calc += ` <span class="font-bold">${this.data.values.selected_trigger_value}${
              selected_trigger_obj?.trigger_value_input_suffix?.length > 1 ? " " : ""
            }${selected_trigger_obj?.trigger_value_input_suffix ? selected_trigger_obj?.trigger_value_input_suffix : ""}</span>`
          }
        }
        if (this.data.values?.sub_item == "metric" && this.data.values.metric_filter) {
          const metric_filter = this.data.values.metric_filter
          const metric_obj = transformServerFilterToMetricFilter(metric_filter, true).filter[1]
          const metric_name = metric_obj?.name
          const operator_text = metric_obj?.operator.label
          text_calc += ` <span class="font-bold"> ${metric_name} </span>`
          let oprator_and_value_text_calc = ""
          if (operator_text?.includes("X")) {
            oprator_and_value_text_calc = `${operator_text.replace("X", ` <span class='font-bold'> ${metric_obj?.value} </span>`)}`
          } else {
            oprator_and_value_text_calc = `${operator_text} <span class='font-bold'> ${metric_obj?.value} </span>`
          }
          text_calc += oprator_and_value_text_calc
        }
      }
      if (this.data.type == "action") {
        if (!this.data.values.meta) {
          if (this.data.values?.sub_item == "change_metric") {
            const metric_filter = this.data.values.metric_filter
            if (metric_filter) {
              const metric_obj = transformServerFilterToMetricFilter(metric_filter, true, true)?.filter[1]
              const operator_text = metric_obj?.operator?.label
              let oprator_and_value_text_calc = ""
              text_calc += `<span class='font-bold'>${metric_obj?.name}</span>`
              if (operator_text?.includes("X")) {
                oprator_and_value_text_calc = ` ${operator_text.replace("X", ` <span class='font-bold'> ${metric_obj?.value} </span>`)}`
              } else {
                let value = metric_obj?.value
                if (this.$store.state.data.tags.find((t) => t.id == metric_obj?.value)) {
                  value = this.$store.state.data.tags.find((t) => t.id == metric_obj?.value).name
                }
                oprator_and_value_text_calc = ` ${operator_text} <span class='font-bold'> ${value} </span>`
              }
              text_calc += oprator_and_value_text_calc
            } else {
              text_calc += " (" + this.$t("Click to configure action") + ")"
            }
          } else if (this.data.values?.sub_item == "send_slack") {
            text_calc += `Click to edit slack message`
          } else if (this.data.values?.sub_item == "call_ai_agent") {
            text_calc += `Click to edit AI agent`
          } else {
            if (this.data.values.filter) {
              text_calc += `${this.data.values.filter.replaceAll("( ", "").replaceAll(" )", "")}`
            } else if (this.data.values.condition) {
              text_calc += this.$t("Click to change filter")
            } else {
              text_calc += " ()" + this.$t("Click to configure action") + ") "
            }
          }
        } else {
          text_calc += ` ${this.data.values.meta.name} `
        }
      }
      if (this.data.type == "wait") {
        if (this.data.values.selected_delay_duration && this.data.values.selected_duration_value) {
          text_calc += `  <span class='font-bold'> ${this.data.values.selected_duration_value} </span> ${this.data.values.selected_delay_duration} `
        } else {
          text_calc += " (" + this.$t("Click to configure delay") + ") "
        }
      }
      text_calc += "</span>"
      return text_calc
    },
    node_title() {
      let end_branch_connected_to_switch = false
      if (this.data.type == "for") {
        return this.$t("Start For Loop")
      }
      if (this.data.type == "end_branch" || this.data.type == "end_parallel") {
        const element_connected_to_end_branch = this.elements.find((el) => el.target == this.id)?.sourceNode
        if (element_connected_to_end_branch?.data?.type == "switch") {
          end_branch_connected_to_switch = true
        }
      }
      if (this.data.values?.sub_item == "metric") {
        if (this.data.values.metric_filter) {
          const metric_filter = this.data.values.metric_filter
          const metric_obj = transformServerFilterToMetricFilter(metric_filter, true).filter[1]
          const object_type = metric_obj?.object_type
          return this.$t("Metric for") + `  ${this.$utils.makePlural(object_type)}`
        } else {
          return this.$t("Metric")
        }
      } else if (this.data.type == "condition") {
        return `${this.data.values.condition ? this.data.values.condition : this.$t("(no condition set)")}`
      } else if (end_branch_connected_to_switch) {
        return this.$t("End Switch")
      } else if (this.data.type == "parallel") {
        return this.$t("Parallel Execution")
      } else {
        if (this.data.values.action_name) {
          return this.data.values.action_name
        }
        if (this.data.values.trigger_name) {
          return this.data.values.trigger_name
        } else if (this.data.values?.selected_items?.length) {
          return `${this.data.values?.selected_items?.length} ${
            this.data.values?.selected_items?.length > 1
              ? this.$utils.makePlural(this.data.values?.sub_item.replaceAll("_", " "))
              : this.data.values?.sub_item.replaceAll("_", " ")
          }`
        } else {
          return this.data.values?.sub_item
            ? `${
                this.data.values.selected_trigger && this.data.values?.sub_item !== "change_co_owners" ? this.$t("Any ") : ""
              }${this.data.values?.sub_item.replaceAll("_", " ")}`
            : this.data?.type?.replaceAll("_", " ")
        }
      }
    }
  },
  watch: {
    filtered_company_id: {
      deep: true,
      handler() {
        this.$store.dispatch("data/getRecord", this.filteredCompanyId).then((res) => {
          this.record_here = res.data
        })
      }
    }
  },
  methods: {
    getRecord(id) {
      this.$store.dispatch("data/getRecord", id).then((res) => {
        this.record_here = res.data
        return res.data
      })
    }
  }
}
</script>

<style scoped>
.yellow-bottom-shadow {
  border-radius: 100%;
  box-shadow: 0px -25px 28px -20px #f9e27150;
}
.red-bottom-shadow {
  border-radius: 100%;
  box-shadow: 0px -25px 28px -20px #f9717150;
}
.blue-bottom-shadow {
  border-radius: 100%;
  box-shadow: 0px -25px 28px -20px #59bee550;
}
.purple-bottom-shadow {
  border-radius: 100%;
  box-shadow: 0px -25px 28px -20px #9747ff50;
}
.green-bottom-shadow {
  border-radius: 100%;
  box-shadow: 0px -25px 28px -20px #45d72d50;
}
.navy-bottom-shadow {
  border-radius: 100%;
  box-shadow: 0px -25px 28px -20px #35657850;
}
.magenta-bottom-shadow {
  border-radius: 100%;
  box-shadow: 0px -25px 28px -20px #ff1bf650;
}
</style>
